import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpRequest } from '@angular/common/http';
import * as CarTypeModels from '../../models/car/carType';
import * as CarModels from '../../models/car/car';
import * as CustomerModels from '../../models/customer/customer';
import { CarSearchModel, CarUpdateModel } from '../../models/car/carSearch';
import { EnvironmentService } from '../../../app/services/environment.service';
import { TooltipModel } from '../../models/tooltip';
import * as Models from '../../models/commonModels';

@Injectable({
  providedIn: 'root'
})
export class CarService {

  constructor(private httpClient: HttpClient,
    private environmentService: EnvironmentService) { }

  public getCarTypeDictionary() {
    return this.httpClient.get<CarTypeModels.CarTypeListModel>(`${this.environmentService.getApiURL()}/car/car-type`);
  }

  public getCar(CarId: number) {
    return this.httpClient.get<CarModels.CarObjectModel>(`${this.environmentService.getApiURL()}/car/car/${CarId}`);
  }

  public setCar(Car: CarModels.CarModel) {

    const httpHeaders = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Cache-Control', 'no-cache');

    const options = { headers: httpHeaders };

    return this.httpClient.post<CarModels.CarModel>(`${this.environmentService.getApiURL()}/car/set-car`, Car, options);
  }

  public get(Search: CarSearchModel) {

    const httpHeaders = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Cache-Control', 'no-cache');

    Search.RequestSignature = Math.random();

    const options = { headers: httpHeaders };
    return this.httpClient.post<CarModels.CarListModel>(`${this.environmentService.getApiURL()}/car/list`, Search, options);
  }

  public getDictionary(Search: CarSearchModel) {

    const httpHeaders = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Cache-Control', 'no-cache'); 

    const options = { headers: httpHeaders };
    return this.httpClient.post<Models.Response<CarModels.CarModel[]>>(`${this.environmentService.getApiURL()}/car/dictionary`, Search, options);
  }

  public insert(Car: Models.Request<CarModels.CarModel>) {
    const httpHeaders = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Cache-Control', 'no-cache');

    const options = { headers: httpHeaders };
    return this.httpClient.post<CarModels.CarModel>(`${this.environmentService.getApiURL()}/car/insert`, Car, options);
  }

  public blockCars(Cars: CarUpdateModel) {
    const httpHeaders = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Cache-Control', 'no-cache');

    const options = { headers: httpHeaders };
    return this.httpClient.post<CarModels.CarUpdateResponse>(`${this.environmentService.getApiURL()}/car/block`, Cars, options);
  }

  public unblockCars(Cars: CarUpdateModel) {
    const httpHeaders = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Cache-Control', 'no-cache');

    const options = { headers: httpHeaders };
    return this.httpClient.post<CarModels.CarUpdateResponse>(`${this.environmentService.getApiURL()}/car/unblock`, Cars, options);
  }

  public changeRegistrations(formData: FormData) {

    return this.httpClient.post<CarModels.CarUpdateResponse>(`${this.environmentService.getApiURL()}/car/list/changeregistrations`, formData);
  }

  public importData(formData: FormData) {

    return this.httpClient.post<CarModels.CarUpdateResponse>(`${this.environmentService.getApiURL()}/car/list/import`, formData);
  }

  public importCars(Data: any) {

    const httpHeaders = new HttpHeaders()
      .set('Cache-Control', 'no-cache');

    return this.httpClient.post<any>(`${this.environmentService.getApiURL()}/car/list/import`, Data, { headers: httpHeaders, responseType: 'blob' as 'json', observe: 'response' });
  }


  public changeCustomer(Cars: CarUpdateModel) {
    const httpHeaders = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Cache-Control', 'no-cache');

    const options = { headers: httpHeaders };
    return this.httpClient.post<CarModels.CarUpdateResponse>(`${this.environmentService.getApiURL()}/car/changecustomer`, Cars, options);
  }


  public getFilteredReport(Search: CarSearchModel) {
    const httpHeaders = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Cache-Control', 'no-cache');

    const options = { headers: httpHeaders, responseType: 'blob' as 'json' };
    return this.httpClient.post<Blob>(`${this.environmentService.getApiURL()}/car/report/filtered`, Search, options);
  }

  public getMarkedReport(Marked: CarUpdateModel) {
    const httpHeaders = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Cache-Control', 'no-cache');

    const options = { headers: httpHeaders, responseType: 'blob' as 'json' };
    return this.httpClient.post<Blob>(`${this.environmentService.getApiURL()}/car/report/marked`, Marked, options);
  }

  public checkNumbers(numbers: string[]) {
    const httpHeaders = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Cache-Control', 'no-cache');
    const options = { headers: httpHeaders };
    return this.httpClient.post<CarModels.CarCheckModel>(`${this.environmentService.getApiURL()}/car/list/check`, numbers, options);
  }

  public getCarTyreDetail(CarId: number) {
    return this.httpClient.get<Models.Response<CarModels.CarTyreDetailModel>>(`${this.environmentService.getApiURL()}/car/tyre/details/${CarId}`);
  }

  public sendEmail(Message: CarModels.CarSendMessage) {
    const httpHeaders = new HttpHeaders()
      .set("Content-Type", "application/json")
      .set("Cache-Control", "no-cache");

    const options = { headers: httpHeaders };

    return this.httpClient.post<Models.ResponseBase>(
      `${this.environmentService.getApiURL()}/car/email/send`,
      Message,
      options
    );
  }

  public sendSMS(Message: CarModels.CarSendMessage) {
    const httpHeaders = new HttpHeaders()
      .set("Content-Type", "application/json")
      .set("Cache-Control", "no-cache");

    const options = { headers: httpHeaders };

    return this.httpClient.post<Models.ResponseBase>(
      `${this.environmentService.getApiURL()}/car/sms/send`,
      Message,
      options
    );
  }

  public getSearchBar(Search: CarSearchModel) {

    const httpHeaders = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Cache-Control', 'no-cache');

    Search.RequestSignature = Math.random();

    const options = { headers: httpHeaders };
    return this.httpClient.post<CarModels.CarListModel>(`${this.environmentService.getApiURL()}/car/search-bar`, Search, options);
  }

  public delete(Car: Models.Request<CarModels.CarModel>) {

    const httpHeaders = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Cache-Control', 'no-cache');

    const options = { headers: httpHeaders };

    return this.httpClient.post<Models.Response<CarModels.CarModel>>(`${this.environmentService.getApiURL()}/car/delete`, Car, options);
  }

  public Create(Car: Models.Request<CarModels.CarModel>) {
    const httpHeaders = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Cache-Control', 'no-cache');

    const options = { headers: httpHeaders };
    return this.httpClient.post<Models.Response<CarModels.CarModel>>(`${this.environmentService.getApiURL()}/car/create`, Car, options);
  }

  public Merge(Cars: Models.ContextRequest<CarModels.CarModel, CarModels.CarModel>) {

    const httpHeaders = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Cache-Control', 'no-cache');

    const options = { headers: httpHeaders };

    return this.httpClient.post<Models.Response<CarModels.CarModel>>(`${this.environmentService.getApiURL()}/car/merge`, Cars, options);
  }

  public sendSMSFiltered(SMS : Models.ContextRequest<CarSearchModel, CarModels.CarSendMessage>) {
    const httpHeaders = new HttpHeaders()
      .set("Content-Type", "application/json")
      .set("Cache-Control", "no-cache");

    const options = { headers: httpHeaders };

    return this.httpClient.post<Models.ResponseBase>(
      `${this.environmentService.getApiURL()}/car/sms/send-filtered`,
      SMS,
      options
    );
  }

  public sendSMSMarked(SMS: Models.ContextRequest<CarUpdateModel, CarModels.CarSendMessage>) {
    const httpHeaders = new HttpHeaders()
      .set("Content-Type", "application/json")
      .set("Cache-Control", "no-cache");

    const options = { headers: httpHeaders };

    return this.httpClient.post<Models.ResponseBase>(
      `${this.environmentService.getApiURL()}/car/sms/send-marked`,
      SMS,
      options
    );
  }

  public getCarNotes(Car: Models.RequestModel) {

    const httpHeaders = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Cache-Control', 'no-cache');

    const options = { headers: httpHeaders };

    return this.httpClient.post<Models.ResponseList<CarModels.CarNoteModel>>(`${this.environmentService.getApiURL()}/car/car-notes`, Car, options);
  }

  public setCarNote(Note: Models.Request<CarModels.CarNoteModel>) {

    const httpHeaders = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Cache-Control', 'no-cache');

    const options = { headers: httpHeaders };

    return this.httpClient.post<Models.ResponseList<CarModels.CarNoteModel>>(`${this.environmentService.getApiURL()}/car/set-car-note`, Note, options);
  }

  public ImportReRegistration(ImportFile: File) {

    const httpHeaders = new HttpHeaders()
      .set('Cache-Control', 'no-cache');

    const formData: FormData = new FormData();
    formData.append('fileKey', ImportFile, ImportFile.name);

    const options = { headers: httpHeaders, responseType: 'blob' as 'json', observe: 'response' };

    return this.httpClient.post<any>(`${this.environmentService.getApiURL()}/car/import-re-registration`, formData, { headers: httpHeaders, responseType: 'blob' as 'json', observe: 'response' });
  }
}
